import { useEffect } from 'react';

import { isFlutterWebView } from './flutterBridge';
import { useIsMobileAuth } from './useMobileAuth';

const WEBVIEW_VIEWPORT =
  'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';

/**
 * Disables pinch-to-zoom / double-tap zoom while the app runs inside the
 * Tunduk (Flutter) WebView. Returns a cleanup function that restores the
 * default behaviour.
 *
 * - viewport meta (`maximum-scale=1, user-scalable=no`) — honoured by Android
 *   WebView and WKWebView (unless the host sets `ignoresViewportScaleLimits`).
 * - `gesturestart`/`gesturechange` preventDefault — iOS fallback where the
 *   viewport meta is ignored.
 * - multi-touch `touchmove` preventDefault — Android fallback.
 * - ctrl/meta + wheel preventDefault — trackpad pinch in desktop WebViews.
 */
export const disablePinchZoom = (): (() => void) => {
  if (typeof document === 'undefined') {
    return () => {};
  }

  const viewport = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
  const prevViewport = viewport?.getAttribute('content') ?? null;

  viewport?.setAttribute('content', WEBVIEW_VIEWPORT);

  const preventDefault = (event: Event) => event.preventDefault();

  const onTouchMove = (event: TouchEvent) => {
    if (event.touches.length > 1) {
      event.preventDefault();
    }
  };

  const onWheel = (event: WheelEvent) => {
    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
    }
  };

  const options: AddEventListenerOptions = { passive: false };

  document.addEventListener('gesturestart', preventDefault, options);
  document.addEventListener('gesturechange', preventDefault, options);
  document.addEventListener('touchmove', onTouchMove, options);
  document.addEventListener('wheel', onWheel, options);

  return () => {
    document.removeEventListener('gesturestart', preventDefault);
    document.removeEventListener('gesturechange', preventDefault);
    document.removeEventListener('touchmove', onTouchMove);
    document.removeEventListener('wheel', onWheel);

    if (viewport && prevViewport !== null) {
      viewport.setAttribute('content', prevViewport);
    }
  };
};

/**
 * Applies {@link disablePinchZoom} while the app is in mobileAuth mode or is
 * hosted by the Flutter WebView bridge.
 */
export const useDisablePinchZoom = (): void => {
  const isMobileAuth = useIsMobileAuth();

  useEffect(() => {
    if (!isMobileAuth && !isFlutterWebView()) {
      return undefined;
    }

    return disablePinchZoom();
  }, [isMobileAuth]);
};
